import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertCircle,
  Trash2, Download, ShieldCheck, Plus, X, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import ImportHistoryDrawer from '../components/mailer/ImportHistoryDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAILER_API = `${BACKEND_URL}/api/mailer`;

// ---------- CSV parser ---------------------------------------------------
function parseCsv(text) {
  const out = [];
  let row = [], cell = '', i = 0, inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cell += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(cell); cell = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(cell); out.push(row); row = []; cell = ''; i++; continue; }
    cell += c; i++;
  }
  if (cell.length || row.length) { row.push(cell); out.push(row); }
  return out.filter((r) => r.some((v) => (v || '').trim() !== ''));
}

// ---------- Canonical field catalog -------------------------------------
// `key` is what the backend expects on MailerRow.
const FIELDS = [
  { key: 'referenceId', label: 'Loan Tracking ID', tone: 'gold' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'name', label: 'Full Name (overrides first+last)' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'loanAmount', label: 'Loan Amount' },
  { key: 'loanDate', label: 'Loan Date' },
  { key: 'lender', label: 'Lender' },
  { key: 'rate', label: 'Rate' },
  { key: 'loanType', label: 'Loan Type' },
  { key: 'term', label: 'Term' },
  { key: 'campaign', label: 'Campaign (overrides batch)' },
  { key: 'source', label: 'Source (overrides batch)' },
  { key: 'loanIntent', label: 'Loan Purpose' },
];
const FIELD_LABELS = Object.fromEntries(FIELDS.map((f) => [f.key, f.label])); // eslint-disable-line no-unused-vars

const REQUIRED_HINT = ['referenceId', 'first_name', 'last_name', 'phone']; // eslint-disable-line no-unused-vars

// Loose auto-mapping. Anything that looks like an alias maps to a canonical
// field; LO can still override in the UI.
const HEADER_ALIASES = {
  name: 'name', full_name: 'name', fullname: 'name',
  first_name: 'first_name', firstname: 'first_name', first: 'first_name', fname: 'first_name',
  last_name: 'last_name', lastname: 'last_name', last: 'last_name', lname: 'last_name', surname: 'last_name',
  phone: 'phone', phone_number: 'phone', mobile: 'phone', cell: 'phone', telephone: 'phone',
  email: 'email', email_address: 'email', e_mail: 'email',
  address: 'address', street: 'address', street_address: 'address', addr: 'address', mailing_address: 'address', property_address: 'address',
  city: 'city', town: 'city',
  state: 'state', state_code: 'state', st: 'state',
  zip: 'zip', zip_code: 'zip', postal: 'zip', postal_code: 'zip', zipcode: 'zip',
  reference_id: 'referenceId', referenceid: 'referenceId', ref_id: 'referenceId', refid: 'referenceId', ref: 'referenceId', refno: 'referenceId', reference: 'referenceId',
  campaign: 'campaign', campaign_name: 'campaign', list: 'campaign', list_name: 'campaign', drop: 'campaign',
  source: 'source',
  loan_intent: 'loanIntent', intent: 'loanIntent', purpose: 'loanIntent', loan_purpose: 'loanIntent',
  loan_amount: 'loanAmount', loanamount: 'loanAmount', amount: 'loanAmount', loan_amt: 'loanAmount',
  est_amount: 'loanAmount', estamount: 'loanAmount',
  loan_date: 'loanDate', loandate: 'loanDate', date: 'loanDate', funding_date: 'loanDate', close_date: 'loanDate',
  lender: 'lender', bank: 'lender', servicer: 'lender',
  rate: 'rate', interest_rate: 'rate', interestrate: 'rate', apr: 'rate',
  loan_type: 'loanType', loantype: 'loanType', type: 'loanType', product: 'loanType', loan_product: 'loanType',
  term: 'term', loan_term: 'term', loanterm: 'term', term_years: 'term', term_yrs: 'term',
};

function normalizeHeader(h) {
  const k = (h || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return HEADER_ALIASES[k] || null;
}

function coerceCell(canon, raw) {
  const v = (raw ?? '').toString().trim();
  if (!v) return null;
  if (canon === 'loanAmount') {
    const n = Number(v.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (canon === 'rate') {
    const n = Number(v.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (canon === 'state') return v.toUpperCase().slice(0, 2);
  return v;
}

// Build the row objects the backend expects, from the raw CSV grid + current
// header->canonical mapping (memoised so live remaps stay fast).
function applyMapping(grid, mapping) {
  if (grid.length < 2) return [];
  const headers = grid[0];
  const rows = grid.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      const canon = mapping[h];
      if (!canon) return;
      const v = coerceCell(canon, cells[idx]);
      if (v != null && v !== '') obj[canon] = v;
    });
    return obj;
  });
  return rows;
}

// ---------- Page --------------------------------------------------------
const TEMPLATE_HEADERS = ['reference_id', 'first_name', 'last_name', 'phone', 'email', 'address', 'city', 'state', 'zip', 'loan_amount', 'loan_date', 'lender', 'rate'];
const TEMPLATE_EXAMPLE = ['CV-MAR26-042', 'Jane', 'Doe', '555-867-5309', 'jane@email.com', '123 Main St', 'Phoenix', 'AZ', '85001', '420000', '05/15/2026', 'UWM', '6.875'];

export default function MailerUpload() {
  const [grid, setGrid] = useState(null);         // [[...headers], [...row], ...]
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState({});     // { csvHeader: 'canonicalKey' | null }
  // Canonical fields the LO has explicitly flagged they want to map. Renders
  // as a chip checklist — turns green when a CSV column gets mapped to it.
  const [wantedFields, setWantedFields] = useState([]); // canonical keys, in add-order
  const [pickAdd, setPickAdd] = useState('');     // staged selection in the Add-field dropdown
  const [campaign, setCampaign] = useState('');
  const [source, setSource] = useState('');
  const [marketingCost, setMarketingCost] = useState('');
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { processed, total, created, refreshed, duplicates }
  const [lastResult, setLastResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const inputRef = useRef(null);

  // Derived: parsed rows from the current mapping (live).
  const rows = useMemo(() => (grid ? applyMapping(grid, mapping) : []), [grid, mapping]);
  const mappedFields = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);
  const unmappedHeaders = useMemo(() => (grid ? grid[0].filter((h) => !mapping[h]) : []), [grid, mapping]);
  // Canonical fields not currently assigned to any CSV column. Powers the
  // "Add field" dropdown — LO can only request fields that exist in the lead
  // schema, and only ones they haven't already mapped.
  const availableForPick = useMemo(
    () => FIELDS.filter((f) => !mappedFields.has(f.key) && !wantedFields.includes(f.key)),
    [mappedFields, wantedFields],
  );
  const missingRequired = useMemo(() => {
    const hasName = mappedFields.has('name') || (mappedFields.has('first_name') && mappedFields.has('last_name'));
    const hasIdentifier = mappedFields.has('referenceId') || mappedFields.has('phone');
    return { hasName, hasIdentifier };
  }, [mappedFields]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') {
      toast.error('Please upload a .csv file');
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length < 2) {
        toast.error('CSV has no data rows');
        return;
      }
      const headers = parsed[0];
      // Build initial auto-mapping. Don't auto-map two headers to the same field.
      const seen = new Set();
      const initial = {};
      for (const h of headers) {
        const canon = normalizeHeader(h);
        if (canon && !seen.has(canon)) {
          initial[h] = canon;
          seen.add(canon);
        } else {
          initial[h] = null;
        }
      }
      setGrid(parsed);
      setFileName(file.name);
      setMapping(initial);
      setLastResult(null);
      const autoCount = Object.values(initial).filter(Boolean).length;
      toast.success(`Parsed ${parsed.length - 1} rows · auto-mapped ${autoCount} of ${headers.length} columns`);
    } catch (e) {
      toast.error('Could not read file: ' + (e?.message || 'unknown error'));
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const reset = () => {
    setGrid(null);
    setFileName('');
    setMapping({});
    setWantedFields([]);
    setPickAdd('');
    setLastResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const remap = (header, value) => {
    setMapping((prev) => {
      const next = { ...prev };
      // If user is assigning a field that's already used by another column,
      // clear the previous owner so each canonical field has at most one source.
      if (value) {
        for (const k of Object.keys(next)) {
          if (next[k] === value && k !== header) next[k] = null;
        }
      }
      next[header] = value || null;
      return next;
    });
  };

  const addWantedField = () => {
    if (!pickAdd) return;
    if (mappedFields.has(pickAdd)) {
      toast.info(`${FIELDS.find((f) => f.key === pickAdd)?.label} is already mapped`);
      setPickAdd('');
      return;
    }
    setWantedFields((prev) => (prev.includes(pickAdd) ? prev : [...prev, pickAdd]));
    setPickAdd('');
  };

  const removeWantedField = (key) => {
    setWantedFields((prev) => prev.filter((k) => k !== key));
  };

  const submit = async () => {
    if (!grid || uploading) return;
    if (!missingRequired.hasName) { toast.error('Map a Name column (or both First + Last)'); return; }
    if (!missingRequired.hasIdentifier) { toast.error('Map a Loan Tracking ID or Phone column'); return; }
    if (!source.trim()) { toast.error('Pick a Lead Source for this batch'); return; }
    setUploading(true);
    setLastResult(null);
    setUploadProgress({ processed: 0, total: rows.length, created: 0, refreshed: 0, duplicates: 0, skipped: 0 });

    const CHUNK = 2000;       // matches backend MAX_CHUNK_ROWS budget comfortably
    const totalRows = rows.length;
    const sourceFinal = source.trim();
    const campaignFinal = campaign.trim() || null;
    // Marketing cost — parsed as a non-negative number; blank means "not
    // tracked". Backend stores as float so reporting can divide spend by
    // total leads to derive cost-per-lead.
    const costNum = Number(String(marketingCost).replace(/[$,\s]/g, ''));
    const marketingCostFinal = isFinite(costNum) && costNum >= 0 && String(marketingCost).trim() !== ''
      ? costNum
      : null;

    try {
      // 1. INIT — open an import session.
      const initRes = await axios.post(`${MAILER_API}/upload/init`, {
        source: sourceFinal,
        campaign: campaignFinal,
        marketing_cost: marketingCostFinal,
        allow_duplicates: allowDuplicates,
        total_rows: totalRows,
      });
      const importId = initRes.data.import_id;

      // 2. CHUNK — stream rows in fixed-size batches.
      let processed = 0;
      let agg = { created: 0, refreshed: 0, duplicates: 0, skipped: 0 };
      let lastTotals = null;
      for (let i = 0; i < totalRows; i += CHUNK) {
        const slice = rows.slice(i, i + CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const r = await axios.post(`${MAILER_API}/upload/chunk`, {
          import_id: importId,
          chunk_index: i / CHUNK,
          rows: slice,
        });
        processed += slice.length;
        lastTotals = r.data.totals || lastTotals;
        agg = {
          created:    lastTotals?.created ?? (agg.created + (r.data.created || 0)),
          refreshed:  lastTotals?.refreshed ?? (agg.refreshed + (r.data.refreshed || 0)),
          duplicates: lastTotals?.duplicates ?? (agg.duplicates + (r.data.duplicates || 0)),
          skipped:    lastTotals?.skipped ?? (agg.skipped + (r.data.skipped || 0)),
        };
        setUploadProgress({ processed, total: totalRows, ...agg });
      }

      // 3. FINALIZE — flip status to completed and pull the final record.
      const finRes = await axios.post(`${MAILER_API}/upload/finalize`, { import_id: importId });
      const final = finRes.data || {};
      setLastResult({
        received: totalRows,
        created: final.created || 0,
        refreshed: final.refreshed || 0,
        duplicates: final.duplicates || 0,
        skipped: final.skipped || 0,
        errors: final.errors || [],
        created_ids: final.created_ids || [],
        refreshed_ids: final.refreshed_ids || [],
        import_id: importId,
      });
      const refreshedPart = final.refreshed ? `, ${final.refreshed} refreshed` : '';
      toast.success(`Uploaded ${final.created} leads (${final.duplicates} dupes${refreshedPart})`);
      window.dispatchEvent(new CustomEvent('kf:leads-changed'));
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const downloadTemplate = () => {
    const csv = [TEMPLATE_HEADERS.join(','), TEMPLATE_EXAMPLE.join(',')].join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'kinfolk-mailer-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-6" data-testid="mailer-upload-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-400 font-bold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" /> Admin · Mailer
          </div>
          <h2 className="display text-[24px] font-bold tracking-tight text-neutral-900 mt-1 leading-none">Upload Leads</h2>
          <p className="text-[13px] text-neutral-500 mt-2 max-w-2xl">
            Drop your mail-house CSV. Rows become <span className="font-semibold text-neutral-700">unclaimed campaign leads</span> tagged with the chosen source so LOs can search by <span className="mono text-[hsl(var(--kf-gold-deep))] font-bold">reference ID</span> and claim them from My Leads → All.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setHistoryOpen(true)}
            data-testid="mailer-import-history-btn"
            className="h-10 px-4 rounded-md border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold text-neutral-700 inline-flex items-center gap-2"
          >
            <History className="h-4 w-4" /> Import history
          </button>
          <button
            onClick={downloadTemplate}
            data-testid="mailer-download-template"
            className="h-10 px-4 rounded-md border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold text-neutral-700 inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" /> CSV template
          </button>
        </div>
      </div>

      {/* Batch tags */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">Campaign name</label>
          <input
            data-testid="mailer-campaign-input"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            placeholder="e.g. Conv-Mar-2026"
            className="mt-1.5 w-full h-11 px-3 rounded-md bg-white border hairline text-[14px] font-semibold text-neutral-900 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition"
          />
          <div className="text-[11px] text-neutral-500 mt-1">Tags every row with this campaign for reporting.</div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">Lead source</label>
          <select
            data-testid="mailer-source-select"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className={cn(
              'mt-1.5 w-full h-11 px-3 rounded-md bg-white border hairline text-[14px] font-semibold focus:outline-none focus:border-neutral-900 transition',
              source ? 'text-neutral-900' : 'text-neutral-400',
            )}
          >
            <option value="" disabled>Select a source…</option>
            {['Conventional Mailer', 'FHA Mailer', 'VA Mailer', 'Web Lead', 'Past Client', 'Other'].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <div className="text-[11px] text-neutral-500 mt-1">Pick the source for this batch. LOs claim by ref ID from the Leads page.</div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">Marketing cost</label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-neutral-400 pointer-events-none">$</span>
            <input
              data-testid="mailer-marketing-cost-input"
              value={marketingCost}
              onChange={(e) => setMarketingCost(e.target.value)}
              placeholder="e.g. 4,500"
              inputMode="decimal"
              className="w-full h-11 pl-7 pr-3 rounded-md bg-white border hairline text-[14px] font-semibold text-neutral-900 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition tabular-nums"
            />
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">Total spend for this campaign. Powers cost-per-lead reporting.</div>
        </div>
      </div>

      {/* Allow-duplicates toggle */}
      <label
        htmlFor="mailer-allow-duplicates"
        className={cn(
          'flex items-start gap-3 px-4 py-3 rounded-lg border hairline cursor-pointer transition',
          allowDuplicates ? 'bg-[hsl(var(--kf-gold-soft))]/40 border-[hsl(var(--kf-gold))]/40' : 'bg-white hover:bg-neutral-50/60'
        )}
        data-testid="mailer-allow-duplicates-row"
      >
        <input
          id="mailer-allow-duplicates"
          type="checkbox"
          checked={allowDuplicates}
          onChange={(e) => setAllowDuplicates(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-neutral-900 cursor-pointer"
          data-testid="mailer-allow-duplicates-checkbox"
        />
        <div className="flex-1">
          <div className="text-[13px] font-bold text-neutral-900">Allow duplicate properties</div>
          <div className="text-[11.5px] text-neutral-500 mt-0.5">
            By default, rows whose <span className="font-semibold text-neutral-700">property address</span> (street, city, state, ZIP) already exists in your leads are matched to that lead and refreshed with the new offer — not duplicated. Turn this on to force-insert them anyway as brand-new leads.
          </div>
        </div>
      </label>

      {/* Drop zone (only before a file is loaded) */}
      {!grid && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="mailer-dropzone"
          className={cn(
            'cursor-pointer rounded-2xl border-2 border-dashed transition flex flex-col items-center justify-center py-16 text-center px-6',
            dragOver ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-300 hover:border-neutral-500 bg-white'
          )}
        >
          <div className="h-12 w-12 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center mb-3">
            <UploadIcon className="h-5 w-5" />
          </div>
          <div className="display text-[20px] font-bold text-neutral-900 tracking-tight">Drop CSV or click to upload</div>
          <div className="text-[12.5px] text-neutral-500 mt-1 max-w-xl">
            We&apos;ll auto-match headers like <span className="mono">reference_id, first_name, address, loan_amount, rate</span>. Anything we can&apos;t match, you can link manually below.
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
            data-testid="mailer-file-input"
          />
        </div>
      )}

      {/* Mapping + Preview */}
      {grid && !lastResult && (
        <div className="space-y-5">
          {/* Mapping card */}
          <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="mailer-mapping">
            <div className="px-5 py-4 border-b hairline bg-neutral-50/40 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-5 w-5 text-[hsl(var(--kf-gold-deep))]" />
                <div>
                  <div className="text-[13.5px] font-bold text-neutral-900">{fileName}</div>
                  <div className="text-[11.5px] text-neutral-500 mono">
                    <span className="font-bold text-neutral-700">{rows.length}</span> rows ·{' '}
                    <span className="font-bold text-neutral-700">{mappedFields.size}</span> mapped fields ·{' '}
                    <span className={unmappedHeaders.length ? 'text-amber-700' : ''}>{unmappedHeaders.length} ignored</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={reset}
                  data-testid="mailer-discard-btn"
                  className="h-9 px-3 rounded-md border hairline bg-white hover:bg-neutral-50 text-[12px] font-semibold text-neutral-600 flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Discard
                </button>
                <button
                  onClick={submit}
                  disabled={uploading || !missingRequired.hasName || !missingRequired.hasIdentifier}
                  data-testid="mailer-upload-btn"
                  title={
                    !missingRequired.hasName ? 'Map a Name (or first+last)'
                    : !missingRequired.hasIdentifier ? 'Map a Loan Tracking ID or Phone'
                    : ''
                  }
                  className="h-9 px-4 rounded-md btn-ink text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UploadIcon className="h-4 w-4" /> {uploading ? 'Uploading…' : `Upload ${rows.length.toLocaleString()} leads`}
                </button>
              </div>
            </div>

            {/* Chunked upload progress — visible only while a batch is in flight. */}
            {uploading && uploadProgress && (
              <div className="px-5 py-3 border-b hairline bg-sky-50/40" data-testid="mailer-upload-progress">
                <div className="flex items-center justify-between text-[11.5px] font-semibold text-neutral-700 mb-1.5">
                  <span>
                    Uploading{' '}
                    <span className="mono">{uploadProgress.processed.toLocaleString()} / {uploadProgress.total.toLocaleString()}</span>{' '}
                    rows
                  </span>
                  <span className="mono text-neutral-500">
                    {Math.round((uploadProgress.processed / Math.max(uploadProgress.total, 1)) * 100)}%
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-neutral-200 overflow-hidden">
                  <div
                    className="h-full bg-sky-500 transition-all duration-200"
                    style={{ width: `${(uploadProgress.processed / Math.max(uploadProgress.total, 1)) * 100}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10.5px] uppercase tracking-wider font-bold text-neutral-500">
                  <span><span className="text-emerald-700">{uploadProgress.created.toLocaleString()}</span> created</span>
                  <span><span className="text-sky-700">{uploadProgress.refreshed.toLocaleString()}</span> refreshed</span>
                  <span><span className="text-amber-700">{uploadProgress.duplicates.toLocaleString()}</span> dupes</span>
                  <span><span className="text-neutral-500">{uploadProgress.skipped.toLocaleString()}</span> skipped</span>
                </div>
              </div>
            )}

            {/* Requirement banner */}
            {(!missingRequired.hasName || !missingRequired.hasIdentifier) && (
              <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 text-[11.5px] text-amber-800 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  Missing required mapping:{' '}
                  {!missingRequired.hasName && <span className="mono font-semibold">Name (or first+last)</span>}
                  {!missingRequired.hasName && !missingRequired.hasIdentifier && ' · '}
                  {!missingRequired.hasIdentifier && <span className="mono font-semibold">Loan Tracking ID or Phone</span>}
                </div>
              </div>
            )}

            <div className="p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-400 font-bold">Column Mapping</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1 mr-3"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> auto-matched</span>
                    <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> needs mapping</span>
                  </div>
                  {availableForPick.length > 0 && (
                    <div className="flex items-center gap-1.5" data-testid="mailer-add-field-picker">
                      <select
                        value={pickAdd}
                        onChange={(e) => setPickAdd(e.target.value)}
                        data-testid="mailer-add-field-select"
                        className="h-8 px-2 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
                      >
                        <option value="">Add a field…</option>
                        {availableForPick.map((f) => (
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={addWantedField}
                        disabled={!pickAdd}
                        data-testid="mailer-add-field-confirm"
                        className="h-8 px-2.5 rounded-md btn-ink text-[11.5px] font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {wantedFields.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="mailer-wanted-field-chips">
                  <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-400 mr-1">To map:</span>
                  {wantedFields.map((key) => {
                    const f = FIELDS.find((x) => x.key === key);
                    if (!f) return null;
                    const isMapped = mappedFields.has(key);
                    return (
                      <span
                        key={key}
                        className={cn(
                          'inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[10.5px] font-bold uppercase tracking-wider',
                          isMapped
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40',
                        )}
                        data-testid={`mailer-wanted-chip-${key}`}
                      >
                        {isMapped && <CheckCircle2 className="h-3 w-3" />}
                        {f.label}
                        <button
                          onClick={() => removeWantedField(key)}
                          className="h-3.5 w-3.5 rounded-full hover:bg-black/10 flex items-center justify-center"
                          aria-label={`Remove ${f.label}`}
                          data-testid={`mailer-wanted-remove-${key}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {grid[0].map((header) => {
                  const value = mapping[header] || '';
                  const isAuto = !!value;
                  return (
                    <div
                      key={header}
                      className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-b-0"
                      data-testid={`mailer-mapping-row-${header}`}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full shrink-0',
                          isAuto ? 'bg-emerald-500' : 'bg-amber-400'
                        )}
                        aria-hidden
                      />
                      <span
                        className={cn(
                          'text-[12.5px] font-semibold mono truncate w-[140px] shrink-0',
                          isAuto ? 'text-neutral-700' : 'text-amber-800'
                        )}
                        title={header}
                      >
                        {header}
                      </span>
                      <span className="text-neutral-300 shrink-0" aria-hidden>→</span>
                      <select
                        data-testid={`mailer-mapping-select-${header}`}
                        value={value}
                        onChange={(e) => remap(header, e.target.value)}
                        className={cn(
                          'flex-1 h-9 px-2.5 rounded-md bg-white border hairline text-[12.5px] font-semibold focus:outline-none focus:border-neutral-900 transition',
                          !value && 'text-amber-700 italic'
                        )}
                      >
                        <option value="">Ignore this column</option>
                        {FIELDS.map((f) => {
                          const used = mappedFields.has(f.key) && mapping[header] !== f.key;
                          return (
                            <option key={f.key} value={f.key} disabled={used}>
                              {f.label}{used ? ' (already used)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-2xl border hairline bg-white overflow-hidden">
            <div className="px-5 py-3 border-b hairline flex items-center justify-between bg-neutral-50/40">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-neutral-400 font-bold">Preview · first 200 rows</div>
              <div className="text-[11px] text-neutral-500">All {rows.length} rows will be uploaded on submit</div>
            </div>
            <PreviewTable rows={rows} mappedFields={mappedFields} />
          </div>
        </div>
      )}

      {/* Result */}
      {lastResult && (
        <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="mailer-result">
          <div className="px-5 py-4 border-b hairline bg-[hsl(var(--kf-gold-soft))]/40 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--kf-gold-deep))]" />
            <div className="flex-1">
              <div className="display text-[18px] font-bold text-neutral-900 tracking-tight">Upload complete</div>
              <div className="text-[12px] text-neutral-600">LOs can now search by ref ID or claim from My Leads → All.</div>
            </div>
            <button
              onClick={reset}
              data-testid="mailer-upload-another"
              className="h-9 px-4 rounded-md btn-ink text-[12.5px] font-bold inline-flex items-center gap-1.5"
            >
              <UploadIcon className="h-4 w-4" /> Upload another
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x hairline">
            {[
              { label: 'Received', value: lastResult.received, tone: 'text-neutral-900' },
              { label: 'Created', value: lastResult.created, tone: 'text-emerald-700' },
              { label: 'Refreshed', value: lastResult.refreshed || 0, tone: 'text-sky-700' },
              { label: 'Duplicates', value: lastResult.duplicates, tone: 'text-amber-700' },
              { label: 'Skipped', value: lastResult.skipped, tone: 'text-neutral-500' },
            ].map((s) => (
              <div key={s.label} className="px-5 py-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">{s.label}</div>
                <div className={cn('display text-[28px] font-bold tracking-tight mt-1', s.tone)}>{s.value}</div>
              </div>
            ))}
          </div>
          {lastResult.errors?.length > 0 && (
            <div className="px-5 py-4 border-t hairline bg-amber-50 text-[12px] text-amber-900">
              <div className="font-bold mb-1.5 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> {lastResult.errors.length} warnings</div>
              <ul className="list-disc list-inside space-y-0.5 mono text-[11px]">
                {lastResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {/* Import history drawer */}
      <ImportHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onChanged={() => window.dispatchEvent(new CustomEvent('kf:leads-changed'))}
      />
    </div>
  );
}

/**
 * Dynamic preview that only renders columns the LO actually mapped. Always
 * keeps the row index.
 */
function PreviewTable({ rows, mappedFields }) {
  // Definition map for canonical fields → render fn + align.
  const COLUMN_DEFS = useMemo(() => ({
    referenceId: { label: 'Tracking ID', render: (r) => <span className="mono text-[11px] text-[hsl(var(--kf-gold-deep))] font-bold">{r.referenceId}</span> },
    name: { label: 'Name', render: (r) => <span className="font-semibold text-neutral-900">{r.name}</span> },
    first_name: { label: 'First', render: (r) => <span className="text-neutral-900 font-semibold">{r.first_name}</span> },
    last_name: { label: 'Last', render: (r) => <span className="text-neutral-900 font-semibold">{r.last_name}</span> },
    phone: { label: 'Phone', render: (r) => <span className="display tracking-tight text-neutral-700">{r.phone}</span> },
    email: { label: 'Email', render: (r) => <span className="text-neutral-600 truncate inline-block max-w-[180px] align-middle">{r.email}</span> },
    address: { label: 'Address', render: (r) => <span className="text-neutral-700 truncate inline-block max-w-[200px] align-middle">{r.address}</span> },
    city: { label: 'City', render: (r) => <span className="text-neutral-700">{r.city}</span> },
    state: { label: 'ST', render: (r) => <span className="mono text-neutral-700">{r.state}</span> },
    zip: { label: 'ZIP', render: (r) => <span className="mono text-neutral-700">{r.zip}</span> },
    loanAmount: { label: 'Loan Amt', align: 'right', render: (r) => <span className="display tracking-tight font-semibold text-neutral-900">${Number(r.loanAmount).toLocaleString('en-US')}</span> },
    lender: { label: 'Lender', render: (r) => <span className="text-neutral-700">{r.lender}</span> },
    rate: { label: 'Rate', align: 'right', render: (r) => <span className="display tracking-tight font-semibold text-neutral-900">{Number(r.rate).toFixed(3)}%</span> },
    loanType: { label: 'Loan Type', render: (r) => <span className="text-neutral-700">{r.loanType}</span> },
    term: { label: 'Term', render: (r) => <span className="text-neutral-700">{r.term}</span> },
    loanDate: { label: 'Loan Date', render: (r) => <span className="display tracking-tight text-neutral-700">{r.loanDate}</span> },
    campaign: { label: 'Campaign', render: (r) => <span className="text-neutral-700">{r.campaign}</span> },
    source: { label: 'Source', render: (r) => <span className="text-neutral-700">{r.source}</span> },
    loanIntent: { label: 'Purpose', render: (r) => <span className="text-neutral-700">{r.loanIntent}</span> },
  }), []);

  // Build the active column list — preserve the canonical order from FIELDS.
  const activeColumns = useMemo(() => {
    const list = [];
    for (const f of FIELDS) {
      if (mappedFields.has(f.key) && COLUMN_DEFS[f.key]) {
        list.push({ key: f.key, ...COLUMN_DEFS[f.key] });
      }
    }
    return list;
  }, [mappedFields, COLUMN_DEFS]);

  // Synthesize a virtual `name` value when only first+last are mapped.
  const fillName = (r) => {
    if (r.name) return r;
    const composed = [r.first_name, r.last_name].filter(Boolean).join(' ');
    return composed ? { ...r, name: composed } : r;
  };

  if (activeColumns.length === 0) {
    return (
      <div className="p-10 text-center text-[13px] text-neutral-500">
        Map at least one column above to preview your data.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[480px]">
      <table className="w-full text-[12px]" data-testid="mailer-preview-table">
        <thead className="bg-neutral-50/60 border-b hairline sticky top-0">
          <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
            <th className="px-4 py-2.5 w-10">#</th>
            {activeColumns.map((col) => (
              <th key={col.key} className={cn('px-4 py-2.5 whitespace-nowrap', col.align === 'right' && 'text-right')}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {rows.slice(0, 200).map((rawRow, i) => {
            const r = fillName(rawRow);
            return (
              <tr key={i} className="hover:bg-neutral-50/50" data-testid={`mailer-preview-row-${i}`}>
                <td className="px-4 py-2.5 mono text-neutral-400 text-[10.5px]">{i + 1}</td>
                {activeColumns.map((col) => {
                  const v = r[col.key];
                  const empty = v == null || v === '';
                  return (
                    <td
                      key={col.key}
                      className={cn('px-4 py-2.5 align-middle whitespace-nowrap', col.align === 'right' && 'text-right')}
                    >
                      {empty ? <span className="text-neutral-300">—</span> : col.render(r)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {rows.length > 200 && (
            <tr><td colSpan={activeColumns.length + 1} className="px-4 py-3 text-center text-[11px] text-neutral-500">
              + {rows.length - 200} more rows · preview capped at 200
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
